import type { OntologyModule } from "../../../api/content/ontologyApi";

/** The modules a library a project pins brings, at the version it pins. */
export interface InheritedModules {
  readonly libraryProjectId: string;
  /** `<collection id>@<commit>`. */
  readonly libraryVersion: string;
  /** Whether it is the DMW basis: the library that holds the version's DMW process ontology,
   * as the server marks it, not any library extending it. */
  readonly isDmwBasis: boolean;
  readonly modules: readonly OntologyModule[];
}

/** The project's own modules, then what each pinned library brings, in the server's order. */
export function moduleGroups(modules: readonly OntologyModule[]): {
  readonly own: readonly OntologyModule[];
  readonly inherited: readonly InheritedModules[];
} {
  const inherited = new Map<string, { libraryVersion: string; modules: OntologyModule[] }>();
  for (const module of modules) {
    if (module.library === null) continue;
    const group = inherited.get(module.library.projectId) ?? { libraryVersion: module.library.version, modules: [] };
    group.modules.push(module);
    inherited.set(module.library.projectId, group);
  }
  return {
    own: modules.filter((module) => module.library === null),
    inherited: [...inherited].map(([libraryProjectId, group]) => ({
      libraryProjectId,
      libraryVersion: group.libraryVersion,
      isDmwBasis: group.modules.some((module) => module.core),
      modules: group.modules,
    })),
  };
}

/** The view to show a picked term in, one that holds it: the project's own ("All", `null`)
 * for a term one of its modules declares, else the inherited module that declares it, such as
 * the DMW basis's (Sol r1, r2). `termModule` is the first module declaring it, own first. */
export function moduleShowing(modules: readonly OntologyModule[], termModule: string): string | null {
  const declaring = modules.find((module) => module.reference === termModule);
  return declaring === undefined || declaring.library === null ? null : declaring.itemId;
}
